/**
 * TwoColumn component - Creates side-by-side layout for PDF
 *
 * Used for client info (left) and account summary (right) sections.
 */

import type { Content, ContentColumns, Column } from 'pdfmake/interfaces';

export interface TwoColumnOptions {
    /** Width distribution - default is ['*', 'auto'] meaning left fills, right is content-sized */
    widths?: ('*' | 'auto' | number)[];
    /** Gap between columns in points */
    columnGap?: number;
    /** Margin around the columns section */
    margin?: [number, number, number, number];
}

/**
 * Create a two-column layout
 *
 * @param left - Content for the left column
 * @param right - Content for the right column
 * @param options - Layout options
 * @returns pdfmake ContentColumns object
 */
export function createTwoColumn(
    left: Content | Content[],
    right: Content | Content[],
    options: TwoColumnOptions = {}
): ContentColumns {
    const {
        widths = ['*', 'auto'],
        columnGap = 20,
        margin,
    } = options;

    // Ensure left content is wrapped in a stack if it's an array
    const leftColumn: Column = Array.isArray(left) ? { stack: left, width: widths[0] } : { ...left as object, width: widths[0] } as Column;
    const rightColumn: Column = Array.isArray(right) ? { stack: right, width: widths[1] } : { ...right as object, width: widths[1] } as Column;

    const result: ContentColumns = {
        columns: [leftColumn, rightColumn],
        columnGap,
    };

    if (margin) {
        result.margin = margin;
    }

    return result;
}

/**
 * Create a three-column layout
 */
export function createThreeColumn(
    left: Content | Content[],
    middle: Content | Content[],
    right: Content | Content[],
    options: { widths?: ('*' | 'auto' | number)[]; columnGap?: number; margin?: [number, number, number, number] } = {}
): ContentColumns {
    const {
        widths = ['*', '*', '*'],
        columnGap = 10,
        margin,
    } = options;

    const leftColumn: Column = Array.isArray(left) ? { stack: left, width: widths[0] } : { ...left as object, width: widths[0] } as Column;
    const middleColumn: Column = Array.isArray(middle) ? { stack: middle, width: widths[1] } : { ...middle as object, width: widths[1] } as Column;
    const rightColumn: Column = Array.isArray(right) ? { stack: right, width: widths[2] } : { ...right as object, width: widths[2] } as Column;

    const result: ContentColumns = {
        columns: [leftColumn, middleColumn, rightColumn],
        columnGap,
    };

    if (margin) {
        result.margin = margin;
    }

    return result;
}

export default createTwoColumn;
